import { useGoogleAccessToken } from '@/composables/useGoogleAccessToken'

type QueryParamValue = string | number | boolean | string[] | undefined

// Builds a query string, appending array values as repeated keys (e.g. resourceNames, sources)
export function toSearchParams (params: Record<string, QueryParamValue>): URLSearchParams {
    const searchParams = new URLSearchParams()

    for (const [key, value] of Object.entries(params)) {
        if (value === undefined) {
            continue
        }

        if (Array.isArray(value)) {
            for (const item of value) {
                searchParams.append(key, item)
            }
        } else {
            searchParams.append(key, String(value))
        }
    }

    return searchParams
}

// Shape (status/result.error.message) matches what convertToGoogleError() already parses from gapi errors
export class GoogleApiResponseError extends Error {
    public readonly status: number
    public readonly statusText: string
    public readonly result: unknown

    constructor (status: number, statusText: string, result: unknown) {
        const errorMessage = (result as { error?: { message?: string } } | undefined)?.error?.message
        super(errorMessage ?? statusText)
        this.name = 'GoogleApiResponseError'
        this.status = status
        this.statusText = statusText
        this.result = result
    }
}

async function performFetch<T> (url: string, init: RequestInit, ignoreExpireTime: boolean, asText = false): Promise<T> {
    const { accessTokenFn } = useGoogleAccessToken()
    const accessToken = await accessTokenFn(ignoreExpireTime)
    if (!accessToken) {
        throw new Error('No access token available')
    }

    const response = await fetch(url, {
        ...init,
        headers: {
            ...init.headers,
            Authorization: `Bearer ${accessToken}`,
        },
    })

    const body = asText
        ? await response.text().catch(() => undefined)
        : await response.json().catch(() => undefined)

    if (!response.ok) {
        throw new GoogleApiResponseError(response.status, response.statusText, body)
    }

    return body as T
}

export async function googleFetch<T> (url: string, init: RequestInit = {}, asText = false): Promise<T> {
    try {
        return await performFetch<T>(url, init, false, asText)
    } catch (error) {
        // 401: the cached access token was rejected, so force a refresh and retry once
        if (error instanceof GoogleApiResponseError && error.status === 401) {
            return await performFetch<T>(url, init, true, asText)
        }

        throw error
    }
}
